import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonChip,
  IonText,
} from '@ionic/react';
import {
  calendarOutline,
  locationOutline,
  peopleOutline,
  ticketOutline,
  cartOutline,
  trendingUpOutline,
  trendingDownOutline,
} from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { eventService, saleService, fulfillmentService } from '../services/api';
import { Event, Sale } from '../types';
import InventoryContent from '../components/InventoryContent';
import SalesContent from '../components/SalesContent';
import CenteredLoader from '../components/CenteredLoader';
import FulfillmentNotification from '../components/FulfillmentNotification';
import {
  getCurrencySymbol,
  DEFAULT_CURRENCY,
} from '../utils/currencyConstants';

interface RouteParams {
  eventId: string;
}

const EventDetails: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams<RouteParams>();
  const { eventId } = params;
  const history = useHistory();

  // Fallback: Extract eventId directly from URL if useParams fails
  const fallbackEventId = React.useMemo(() => {
    const pathParts = window.location.pathname.split('/');
    const eventsIndex = pathParts.findIndex((part) => part === 'events');
    if (eventsIndex !== -1 && pathParts[eventsIndex + 1]) {
      return pathParts[eventsIndex + 1];
    }
    return null;
  }, []);

  // Use fallback if primary eventId is undefined
  const actualEventId = eventId || fallbackEventId || undefined;
  console.log('EventDetails - actualEventId (with fallback):', actualEventId);
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState<'inventory' | 'sales'>(
    'inventory'
  );
  const [sales, setSales] = useState<Sale[]>([]);
  const [saleFulfillmentDetails, setSaleFulfillmentDetails] = useState<{
    [key: string]: any;
  }>({});
  const [profitLoss, setProfitLoss] = useState<number>(0);

  useEffect(() => {
    loadEvent();
    loadSalesAndCalculateProfit();
  }, [actualEventId]);

  const hasInventoryConnection = (sale: Sale): boolean => {
    const saleId = sale.id || sale._id || '';
    const fulfillmentDetails = saleFulfillmentDetails[saleId];

    return (
      (fulfillmentDetails && fulfillmentDetails.totalFulfilled > 0) ||
      (sale.ticketIds && sale.ticketIds.length > 0)
    );
  };

  const calculateProfitLoss = (salesData: Sale[]): number => {
    return salesData
      .filter(
        (sale) => sale.status === 'confirmed' && hasInventoryConnection(sale)
      )
      .reduce((total, sale) => total + sale.netProfit, 0);
  };

  const loadSalesAndCalculateProfit = async () => {
    try {
      if (!actualEventId || actualEventId === 'undefined') {
        console.error(
          'EventDetails - Invalid actualEventId in loadSalesAndCalculateProfit:',
          actualEventId
        );
        return;
      }

      const salesData = await saleService.getAll({ eventId: actualEventId });
      const salesArray = Array.isArray(salesData) ? salesData : [];
      setSales(salesArray);

      // Load fulfillment details for completed sales
      const fulfillmentPromises = salesArray
        .filter((sale) => sale.status === 'confirmed')
        .map(async (sale) => {
          const saleId = sale.id || sale._id;
          if (saleId) {
            try {
              const details = await fulfillmentService.getSaleFulfillment(
                saleId
              );
              return { saleId, details };
            } catch (err) {
              return { saleId, details: null };
            }
          }
          return null;
        });

      const fulfillmentResults = await Promise.all(fulfillmentPromises);
      const detailsMap: { [key: string]: any } = {};
      fulfillmentResults.forEach((result) => {
        if (result && result.saleId) {
          detailsMap[result.saleId] = result.details;
        }
      });
      setSaleFulfillmentDetails(detailsMap);
    } catch (error) {
      console.error('Failed to load sales:', error);
    }
  };

  // Recalculate profit when fulfillment details change
  useEffect(() => {
    if (sales.length > 0) {
      const totalProfitLoss = calculateProfitLoss(sales);
      setProfitLoss(totalProfitLoss);
    }
  }, [sales, saleFulfillmentDetails]);

  const loadEvent = async () => {
    try {
      setLoading(true);

      if (!actualEventId || actualEventId === 'undefined') {
        console.error('EventDetails - Invalid actualEventId:', actualEventId);
        history.goBack();
        return;
      }
      const eventData = await eventService.getEvent(actualEventId);
      setEvent(eventData);
    } catch (error) {
      console.error('Failed to load event:', error);
      history.goBack();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/events" />
            </IonButtons>
            <IonTitle>{t('events.eventDetails')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <CenteredLoader message={t('events.loadingEvent')} />
        </IonContent>
      </IonPage>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/events" />
          </IonButtons>
          <IonTitle>{event.name}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        {/* Event Info Card */}
        <IonCard className="m-4">
          <IonCardContent>
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {event.name}
              </h2>

              <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                <IonIcon icon={calendarOutline} className="text-lg" />
                <span>{format(new Date(event.date), 'MMMM dd, yyyy')}</span>
                {event.time && <span>• {event.time}</span>}
              </div>

              <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                <IonIcon icon={locationOutline} className="text-lg" />
                <span>{event.venue}</span>
              </div>

              {event.totalTickets !== undefined && (
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <IonIcon icon={ticketOutline} className="text-lg" />
                  <span>
                    {t('events.totalTickets')}: {event.totalTickets}
                  </span>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <IonChip
                  color={event.status === 'active' ? 'success' : 'medium'}
                  className="m-0"
                >
                  <IonLabel className="capitalize">
                    {t(`events.status.${event.status}`)}
                  </IonLabel>
                </IonChip>
              </div>
            </div>
          </IonCardContent>
        </IonCard>

        {/* Profit/Loss Card */}
        <IonCard className="m-4">
          <IonCardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <IonIcon
                  icon={
                    profitLoss >= 0 ? trendingUpOutline : trendingDownOutline
                  }
                  className={`text-2xl ${
                    profitLoss >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Match Profit/Loss
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    From connected sales only
                  </p>
                </div>
              </div>
              <div
                className={`text-right ${
                  profitLoss >= 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                <div className="text-2xl font-bold">
                  {profitLoss >= 0 ? '+' : ''}
                  {getCurrencySymbol(DEFAULT_CURRENCY)}
                  {Math.abs(profitLoss).toFixed(2)}
                </div>
                <div className="text-sm">
                  {profitLoss >= 0 ? 'Profit' : 'Loss'}
                </div>
              </div>
            </div>
          </IonCardContent>
        </IonCard>

        {/* Segment Control */}
        <div className="px-4 mb-4">
          <IonSegment
            value={selectedSegment}
            onIonChange={(e) =>
              setSelectedSegment(e.detail.value as 'inventory' | 'sales')
            }
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm"
          >
            <IonSegmentButton value="inventory">
              <IonLabel>{t('inventory.title')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="sales">
              <IonLabel>{t('sales.title')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>

        {/* Segment Content */}
        <div className="">
          <div
            style={{
              display: selectedSegment === 'inventory' ? 'block' : 'none',
            }}
          >
            <InventoryContent event={event} />
          </div>
          <div
            style={{ display: selectedSegment === 'sales' ? 'block' : 'none' }}
          >
            <SalesContent event={event} />
          </div>
        </div>
      </IonContent>

      {/* Fulfillment Notification */}
      <FulfillmentNotification />
    </IonPage>
  );
};

export default EventDetails;
